import React, { useState, useEffect, startTransition, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './AllyNavigation.scss';

import NotificationDrawerItem from './NotificationDrawerItem';
import {
  updateSocketId,
  setInitialState,
  fetchInitialStateAsync,
  setNavigationDrawerCollapsed,
} from '../AssetGroupsSlice';
import {
  setExecuteControlActionOnReturnSuccessful,
  setExecuteControlActionOnReturnError,
} from '../../asset/AssetDataSlice';
import { useAppDispatch } from '../../../hooks/storeHooks';
import { getWebSocketURI } from '../../../utilities/BaseURLUtility';
import { WellControlNotification, addNotificationsFromWellControlTransaction } from '../NotificationsSlice';
import { incrementEventCount } from '../EventCounterSlice';
import AuthService from '../../../services/authentication/auth.service';
import { setInitialState as setInitialTrendLibraryState } from '../../asset-analysis/TrendGroupLabel/TrendGroupLabelSlice';
import { setInitialState as setInitialCoordinateState } from '../../data-history/TrendLibrary/TrendCoordinateSlice';
import { updateCheckBoxFlag } from '../../data-history/TrendLibrary/TrendLibrarySlice';
import { useAppSelector } from '../../../hooks/storeHooks';
import TokenService from '../../../services/authentication/token.service';
import { useLoginSettings, useSettingUrls } from '../../../hooks/useSettingUrls';
import useConfig from '../../../hooks/useConfig';
import { fetchNavigationData, setNavMenuTap } from '../NavigationSlice';

// Icons Import
import { ReactComponent as ProductionIcon } from '../../../images/droplets-03.svg';
import { ReactComponent as RunlifeIcon } from '../../../images/zap-fast.svg';
import { ReactComponent as DowntimeIcon } from '../../../images/slash-circle-01.svg';
import { ReactComponent as WellHealthIcon } from '../../../images/heart.svg';
import { ReactComponent as MenuIcon } from '../../../images/menu-icon.svg';
import { ReactComponent as RecommendationsIcon } from '../../../images/lightbulb-03.svg';
import { ReactComponent as TasksIcon } from '../../../images/check-square-broken.svg';
import { ReactComponent as CustomViewIcon } from '../../../images/custom-dash-icon-3.svg';
import { ReactComponent as NotificationIcon } from '../../../images/bell-01.svg';
import { ReactComponent as WellDirectoryIcon } from '../../../images/dataflow-02.svg';
import { ReactComponent as CollapseIcon } from '../../../images/log-out-02.svg';
import { ReactComponent as InfoCircleIcon } from '../../../images/info-circle-1.svg';
// import { ReactComponent as SettingsIcon } from '../../../images/settings-02.svg';
import { ReactComponent as OnboardingIcon } from '../../../images/onboarding-icon.svg';
import { ReactComponent as PasswordChangeIcon } from '../../../images/password-icon.svg';
import { ReactComponent as ChevronRightIcon } from '../../../images/chevron-right.svg';
import SLBLogo from '../../../images/slb-logo-png.png';
import { useParameterGroups } from '../../../hooks/useParameterGroups';
import { RootState } from '../../../store';
import { setOperation } from '../../setpoints-manager/parameterGroupsSlice';
import { Card, addSingleCardCoordinates } from '../../rod-lift-analysis/CardCoordinatesSlice';
import { CardDateItemState, addSingleCardItem } from '../../rod-lift-analysis/CardDateSlice';
import {
  addOrUpdateNodeIdStatus,
  incrementScanProgressCount,
  markWellScanComplete,
  setScanProgressCount,
} from '../../custom-views/MultiWellControlConfirm/WellControlSlice';
import { useToastService } from '../../../components/toast/ToastService';
import { addLiveDataPoints } from '../../../components/slices/AssetTrendDataSlice';
import { store } from '../../../store';

// These numbers map to the value for socket response expected by well control on the backend.
enum Action {
  Unknown = 0,
  ProcessDeviceScan = 1,
  StartWell = 2,
  StopWell = 3,
  IdleWell = 4,
  ClearAlarms = 5,
  CommunicationLog = 18,
  ConstantRunMode = 6,
  PocMode = 7,
  PercentTimerMode = 8,
  SetClock = 9,
  UpdatePocData = 10,
  GetCard = 11,
  DownloadEquipment = 12,
  UploadEquipment = 13,
  EnablePid = 14,
  DisablePid = 15,
  GetData = 16,
  GetCardDirect = 17,
  CaptureRegisterLogs = 19,
  OnOffCycles = 20,
  StartPortLogging = 21,
  StopPortLogging = 22,
}

export const getIconComponent = (key: string) => {
  const iconMap: { [key: string]: React.ComponentType<any> } = {
    production: ProductionIcon,
    runlife: RunlifeIcon,
    downtime: DowntimeIcon,
    wellHealth: WellHealthIcon,
    exceptions: InfoCircleIcon,
    recommendations: RecommendationsIcon,
    tasks: TasksIcon,
    wellDirectory: WellDirectoryIcon,
    customViews: CustomViewIcon,
    notifications: NotificationIcon,
    support: InfoCircleIcon,
    // settings: SettingsIcon,
    changePassword: PasswordChangeIcon,
    signOut: CollapseIcon,
    logout: CollapseIcon,
    onboarding: OnboardingIcon,
    dashboards: MenuIcon,
  };

  return iconMap[key] || NotificationIcon;
};

const AllyNavigation: React.FC = () => {
  const dispatch = useAppDispatch();
  const { updatedRows } = useParameterGroups();
  const navigate = useNavigate();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandNotificationDrawer, setExpandNotificationDrawer] = useState(false);
  const [preventAutoExpand, setPreventAutoExpand] = useState(false);
  const [mouseHasLeftNav, setMouseHasLeftNav] = useState(true);
  const [expandedSubMenu, setExpandedSubMenu] = useState<string | null>(null);
  const [isMouseOverSubMenu, setIsMouseOverSubMenu] = useState(false);
  const prevControlModeAction = useAppSelector((state) => state.assetData.previousControlModeAction);
  const groupName = useAppSelector((state) => state.assetGroups?.selectedGroup?.assetGroupName);
  const isAdmin = TokenService.getIsAdmin();
  const isSSOUser = TokenService.getIsSSOUser();
  const { allyOnboardingURL } = useSettingUrls();
  const { loginURL, isConnexiaLogin } = useLoginSettings();
  const { isCloud, hostingEnvironment } = useConfig();
  const operation = useAppSelector((state: RootState) => state.parameterGroups.operation);
  // Navigation state from API
  const navigationItems = useAppSelector((state) => state.AllyNavigationSlice.items);
  const footerItems = useAppSelector((state) => state.AllyNavigationSlice.footer);
  const navigationLoading = useAppSelector((state) => state.AllyNavigationSlice.loading);
  const navigationError = useAppSelector((state) => state.AllyNavigationSlice.error);
  const navMenuTap = useAppSelector((state) => state.AllyNavigationSlice.navMenuTap);

  const ref = useRef<HTMLDivElement>(null);

  //WebSocket and Heartbeat
  const socketRef = useRef<WebSocket | null>(null);
  const heartbeatTimerRef = useRef<NodeJS.Timeout | null>(null);
  const eventSocketRef = useRef<WebSocket | null>(null);
  const eventHeartbeatTimerRef = useRef<NodeJS.Timeout | null>(null);
  const wsHeartBeatDuration = 60000;
  const wsReconnectDelay = 60000;

  const showLabels = isExpanded && !expandNotificationDrawer;
  const { showSuccessToast, showErrorToast } = useToastService();
  const isCustomViewsPage = window.location.pathname.startsWith('/group/customViews');
  const operationRef = useRef(operation);

  useEffect(() => {
    operationRef.current = operation;
  }, [operation]);

  useEffect(() => {
    dispatch(fetchNavigationData());
  }, [dispatch]);

  // Custom click outside effect that considers both navigation and drawers
  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      handleClickOutside(event);
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, []);

  // Collapse sidebar on route change to ensure consistency across pages
  useEffect(() => {
    setIsExpanded(false);
    setExpandedSubMenu(null);
    setExpandNotificationDrawer(false);
  }, [location.pathname]);

  // Handle drawer state changes to prevent auto-expansion
  useEffect(() => {
    // When notification drawer is closed, set prevention and clear after delay
    if (!expandNotificationDrawer) {
      setPreventAutoExpand(true);
      // Also ensure expanded state is false when drawer closes
      setIsExpanded(false);
      // Reset mouse tracking to require user to re-enter nav area
      setMouseHasLeftNav(true);
      const timer = setTimeout(() => {
        setPreventAutoExpand(false);
      }, 500);

      return () => clearTimeout(timer);
    } else {
      // Close sub-menu when drawer opens
      setExpandedSubMenu(null);
    }
    // No cleanup needed when drawer is open
    return undefined;
  }, [expandNotificationDrawer]);

  const handleClickOutside = (event: MouseEvent) => {
    // Check if click is inside navigation bar
    if (ref.current && ref.current.contains(event.target as Node)) {
      return;
    }

    // Check if click is inside sub-menu
    const subMenuElements = document.querySelectorAll('.navigation-bar2-submenu');
    for (const subMenu of subMenuElements) {
      if (subMenu.contains(event.target as Node)) {
        return; // Don't close if clicking inside sub-menu
      }
    }

    // Check if click is inside any drawer
    const drawerElements = document.querySelectorAll(
      '[data-testid="asset-navigation-drawer"], [data-testid="notification-drawer"]',
    );
    for (const drawer of drawerElements) {
      if (drawer.contains(event.target as Node)) {
        return; // Don't close if clicking inside a drawer
      }
    }

    if (window.innerWidth <= 1024) {
      dispatch(setNavMenuTap(false));
    }

    // Close sub-menu and drawers when clicking outside
    handleCloseDrawer();
  };

  const handleCloseDrawer = () => {
    setExpandedSubMenu(null);
    setExpandNotificationDrawer(false);
    setIsExpanded(false);
  };

  useEffect(() => {
    // Set up auto-refresh every 3 minutes
    const refreshInterval = setInterval(() => {
      dispatch(fetchInitialStateAsync());
    }, 180000);

    const timerId = setInterval(() => {
      if (!AuthService.refreshTokenFromServer()) {
        isConnexiaLogin || !isCloud ? AuthService.logout(loginURL) : AuthService.formLogout();
      }
    }, 900000);
    return () => {
      clearInterval(refreshInterval);
      clearInterval(timerId);
    };
  }, []);

  const getPrevMode = (currentControlActionValue: number) => {
    if (!prevControlModeAction || currentControlActionValue === prevControlModeAction.controlActionType) return 'Mode';
    const actionValue = prevControlModeAction.controlActionType;
    let prevMessage = '';
    switch (Number(actionValue)) {
      case Action.ConstantRunMode:
        prevMessage = `Constant run mode`;
        break;
      case Action.PocMode:
        prevMessage = `POC mode`;
        break;
      case Action.PercentTimerMode:
        prevMessage = `Percent time mode`;
        break;
      default:
        prevMessage = `Mode`;
    }
    return prevMessage;
  };

  const getUpdatedToastMessage = (controlActionType: number, wellName = 'well') => {
    let returnMessage = '';
    switch (controlActionType) {
      case Action.StartWell:
        returnMessage = `${wellName} successfully started`;
        break;
      case Action.StopWell:
        returnMessage = `${wellName} successfully shutdown`;
        break;
      case Action.ClearAlarms:
        returnMessage = `Alarms successfully cleared for ${wellName}`;
        break;
      case Action.SetClock:
        returnMessage = `Clocks successfully reset for ${wellName}`;
        break;
      case Action.ConstantRunMode:
        returnMessage = `${getPrevMode(
          Action.ConstantRunMode,
        )} was successfully changed to Constant run mode for ${wellName}`;
        break;
      case Action.PocMode:
        returnMessage = `${getPrevMode(Action.PocMode)} was successfully changed to POC mode for ${wellName}`;
        break;
      case Action.PercentTimerMode:
        returnMessage = `${getPrevMode(
          Action.PercentTimerMode,
        )} was successfully changed to Percent time mode for ${wellName}`;
        break;
      default:
        returnMessage = `Transaction updated successfully for ${wellName ?? 'well'}`;
    }
    return returnMessage;
  };

  const scannedWellsData = useAppSelector((state: RootState) => state.wellControl.scannedWellsData);
  const scannedWellsRef = useRef(scannedWellsData);

  useEffect(() => {
    scannedWellsRef.current = scannedWellsData;
  }, [scannedWellsData]);

  useEffect(() => {
    const connectWebSocket = () => {
      // Clean up existing socket and timer
      if (socketRef.current) {
        socketRef.current.close();
      }

      if (heartbeatTimerRef.current) {
        clearTimeout(heartbeatTimerRef.current);
      }

      const webSocketURI = getWebSocketURI('wellcontrolws');
      const socket = new WebSocket(webSocketURI);
      socketRef.current = socket;

      const resetHeartbeat = () => {
        if (heartbeatTimerRef.current) {
          clearTimeout(heartbeatTimerRef.current);
        }
        heartbeatTimerRef.current = setTimeout(() => {
          console.log('No message received in 60 seconds, reconnecting WebSocket...');
          connectWebSocket();
        }, wsHeartBeatDuration);
      };

      // Connection opened
      socket.addEventListener('open', (event) => {
        socket.send('Connection established' + (event as unknown as string));
        resetHeartbeat();
      });

      socket.addEventListener('message', (event) => {
        resetHeartbeat();

        if (event.data.startsWith('socketId:')) {
          dispatch(
            updateSocketId({
              socketId: event.data.split(':')[1],
            }),
          );
        } else if (event.data === 'Close') {
          socket.close();
        } else {
          const eventData = event?.data;
          if (
            (typeof eventData === 'string' && (eventData === 'h' || eventData == '' || eventData == 'ping')) ||
            eventData == null
          ) {
            return;
          }
          const parsedData = JSON.parse(eventData);

          if (parsedData.Status == 'Failure') {
            showErrorToast('Failure', parsedData?.StatusMessage || 'An error occurred during the transaction.');
            return;
          }

          if (parsedData.Status == 'Success') {
            const payloadData = JSON.parse(parsedData.Payload);
            if (payloadData && payloadData?.Value) {
              const payloadDetails = JSON.parse(payloadData?.Value?.Payload);
              // TODO : updateSocketCoordinatesData not available on QA
              // const coordinatesData = payloadDetails?.DeviceScanData?.Values;
              // const scanDate = payloadDetails?.DeviceScanData?.UtcScanTime;
              // dispatch(updateSocketCoordinatesData({ coordinates: coordinatesData, scanDate: scanDate }));
              const nodeId = payloadDetails?.NodeId || payloadDetails?.DeviceScanData?.NodeId;

              // Update well scane status
              if (nodeId) {
                const normalizedNodeId = nodeId.trim().toLowerCase();
                const matchedWell = scannedWellsRef.current.find(
                  (well) => well.wellName.trim().toLowerCase() === normalizedNodeId,
                );
                if (matchedWell) {
                  dispatch(markWellScanComplete({ assetId: matchedWell.assetId }));
                }
                dispatch(
                  addOrUpdateNodeIdStatus({
                    nodeId,
                    status: parsedData.Status,
                  }),
                );
              }

              const toastMessage = getUpdatedToastMessage(Number(payloadData.Value?.ActionType), nodeId);
              if (!isCustomViewsPage) {
                showSuccessToast(toastMessage);
              }
              dispatch(setExecuteControlActionOnReturnSuccessful({ message: toastMessage }));
              const normalizedNodeId = nodeId.trim().toLowerCase();
              const matchedWell = scannedWellsRef.current.find(
                (well) => well.wellName.trim().toLowerCase() === normalizedNodeId,
              );
              if (matchedWell) {
                dispatch(markWellScanComplete({ assetId: matchedWell.assetId }));
              }
              dispatch(incrementScanProgressCount());
            }
          }

          if (parsedData.Status == 'Failed') {
            dispatch(incrementScanProgressCount());
          }

          if (parsedData.StatusMessage?.toLowerCase()?.includes('well updated successfully')) {
            const notifications: WellControlNotification[] = [];
            notifications.push(JSON.parse(parsedData.Payload));
            dispatch(
              addNotificationsFromWellControlTransaction({
                notifications: notifications,
              }),
            );
          } else if (
            parsedData.StatusMessage?.toLowerCase()?.includes('transaction updated successfully') ||
            parsedData.StatusMessage?.toLowerCase()?.includes('data retrieved successfully')
          ) {
            if (operationRef.current === 'restore') {
              dispatch(setOperation(''));
              return;
            } else if (parsedData.ActionType === 'write') {
              if (!isCustomViewsPage) {
                showSuccessToast('Setpoints written successfully');
              }
              dispatch(setOperation(''));
              return;
            } else if (parsedData.ActionType === 'read') {
              if (!isCustomViewsPage) {
                showSuccessToast('Controller scan complete');
              }
            }
            const payload = JSON.parse(parsedData.Payload);
            const payloadType = payload.Value?.PayloadType;
            if (payloadType) {
              const payload2 = JSON.parse(payload.Value.Payload);
              switch (payloadType) {
                case 2: {
                  if (
                    payload2.LogEventDataList === null ||
                    (payload2.LogEventDataList.length === 1 && payload2.LogEventDataList[0] === null)
                  )
                    return;

                  const notifications: WellControlNotification[] = payload2.LogEventDataList;
                  dispatch(
                    addNotificationsFromWellControlTransaction({
                      notifications: notifications,
                    }),
                  );
                  break;
                }
                case 4: {
                  // Response from the Get card action
                  const updatedCardDataValues = payload2.UpdatedCardData?.Values;
                  handleGetCardResponse(updatedCardDataValues, true);
                  break;
                }
              }
            } else {
              // dispatch(
              //   setExecuteControlActionOnReturnSuccessful({
              //     message: 'Controller scan complete',
              //   }),
              // );
              updatedRows(payload);
            }
          } else {
            if (parsedData.StatusMessage) {
              dispatch(
                setExecuteControlActionOnReturnError({
                  message: groupName ? `Transaction failed for ${groupName}` : parsedData.StatusMessage,
                }),
              );
            }
          }
        }
      });

      socket.addEventListener('error', (error) => {
        console.error('WebSocket error:', error);
        // Attempt to reconnect on error after a short delay
        setTimeout(() => {
          connectWebSocket();
        }, wsReconnectDelay);
      });
    };

    connectWebSocket();

    // Cleanup function
    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
      if (heartbeatTimerRef.current) {
        clearTimeout(heartbeatTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Event WebSocket connection handler
    const connectEventWebSocket = () => {
      // Clean up existing socket and timer
      if (eventSocketRef.current) {
        eventSocketRef.current.close();
      }

      if (eventHeartbeatTimerRef.current) {
        clearTimeout(eventHeartbeatTimerRef.current);
      }

      const webSocketURI = getWebSocketURI('eventws');
      const socket = new WebSocket(webSocketURI);
      eventSocketRef.current = socket;

      const resetHeartbeat = () => {
        if (eventHeartbeatTimerRef.current) {
          clearTimeout(eventHeartbeatTimerRef.current);
        }
        eventHeartbeatTimerRef.current = setTimeout(() => {
          console.log('No message received in 60 seconds, reconnecting Event WebSocket...');
          connectEventWebSocket();
        }, wsHeartBeatDuration);
      };

      // Connection opened
      socket.addEventListener('open', (event) => {
        socket.send('Event connection established' + (event as unknown as string));
        resetHeartbeat();
      });

      socket.addEventListener('message', (event) => {
        resetHeartbeat();

        const eventData = event?.data;
        if (
          (typeof eventData === 'string' &&
            (eventData === 'h' || eventData === '' || eventData.startsWith('socketId'))) ||
          eventData === null
        ) {
          return;
        }

        try {
          const parsedData = JSON.parse(eventData);

          if (
            parsedData.ActionType != null &&
            parsedData.ActionType !== 'scan' &&
            parsedData.ActionType != 'EventWS-DeviceScanData' &&
            parsedData.ActionType != 'EventWS-GetCard'
          ) {
            // Increment the event counter for badge display
            dispatch(incrementEventCount());
          }

          if (parsedData.StatusMessage === 'ScanProgress') {
            const scannedCount = Number(parsedData.Payload?.ScannedCount ?? 0);
            dispatch(setScanProgressCount(scannedCount));
            return;
          }

          if (parsedData.Status == 'Success') {
            const payloadData = JSON.parse(parsedData.Payload);
            if (payloadData && payloadData?.Value) {
              const payloadDetails = JSON.parse(payloadData?.Value?.Payload);
              const isLiveDataEnabled = store.getState().AssetTrendDataSlice.isLiveDataEnabled;
              if (
                isLiveDataEnabled &&
                payloadDetails?.UpdatedCardData?.Values?.CardDate &&
                payloadDetails?.UpdatedCardData?.Values?.CardType
              ) {
                const cardDataWithNodeId = {
                  ...payloadDetails.UpdatedCardData.Values,
                  NodeId: payloadDetails.NodeId,
                };
                handleLiveCardData(cardDataWithNodeId);
              }

              handleSparklineStreamingData(payloadDetails);
            }
          }
        } catch (error) {
          console.error('Error parsing event data:', error);
        }
      });

      socket.addEventListener('error', (error) => {
        console.error('Event WebSocket error:', error);
        // Attempt to reconnect on error after a short delay
        setTimeout(() => {
          connectEventWebSocket();
        }, wsReconnectDelay);
      });
    };
    connectEventWebSocket();

    // Cleanup function
    return () => {
      if (eventSocketRef.current) {
        eventSocketRef.current.close();
      }
      if (eventHeartbeatTimerRef.current) {
        clearTimeout(eventHeartbeatTimerRef.current);
      }
    };
  }, []);

  const handleNavigationItemClick = (item: { key: string; disabled: boolean; children?: any[] }) => {
    if (item.disabled) {
      return; // Do nothing for disabled items
    }

    // Special handling for notifications - keep current implementation
    if (item.key === 'notifications') {
      handleNotificationClick();
      return;
    }
    if (item.key === 'customViews' || item.key === 'custom-views') {
      dispatch(setNavigationDrawerCollapsed(false));
      navigate(`/group/customViews/${encodeURIComponent(groupName ?? '')}`);
      return;
    }
    if (item.key === 'wellLocation') {
      navigate(`/well-location`);
      return;
    }
    if (item.key === 'recommendations') {
      navigate('/recommendations');
      return;
    }

    // Handle items with sub-menus
    if (item.children && item.children.length > 0) {
      setExpandedSubMenu(expandedSubMenu === item.key ? null : item.key);
      return;
    }

    // Handle regular navigation items (no children)
    // TODO: Add navigation logic for items without children
    console.log('Navigate to:', item.key);
  };

  const handleSubMenuItemClick = (parentKey: string, subItem: { key: string; path: string; disabled: boolean }) => {
    if (subItem.disabled) {
      return;
    }

    // Close any expanded sub-menus when navigating
    setExpandedSubMenu(null);

    // Handle sub-menu navigation
    if (subItem.path) {
      navigate(subItem.path);
    }

    console.log('Navigate to sub-menu:', parentKey, subItem.key, subItem.path);
  };

  const handleNotificationClick = () => {
    const shouldDisplayDrawer = !expandNotificationDrawer;
    setExpandNotificationDrawer(shouldDisplayDrawer);
  };

  const handleOnboardingClick = () => {
    if (allyOnboardingURL) {
      window.open(allyOnboardingURL, '_blank');
    } else {
      console.log('Onboarding URL is not available');
    }
  };

  const handleChangePasswordClick = () => {
    if (loginURL) {
      window.location.assign(`${loginURL}/Account/ResetPassword`);
    } else {
      console.log('change password URL is not available');
    }
  };

  const handleLogoutClick = () => {
    dispatch(setInitialTrendLibraryState());
    dispatch(setInitialCoordinateState());
    dispatch(updateCheckBoxFlag(false));
    dispatch(setInitialState());

    startTransition(() => {
      isConnexiaLogin || !isCloud ? AuthService.logout(loginURL) : AuthService.formLogout();
    });
  };

  const handleGetCardResponse = (updatedCardDataValues: any, shouldSelect: boolean) => {
    // Map card type codes to readable names
    console.log('Card values from ws:', updatedCardDataValues);
    const cardTypeMap: Record<string, string> = {
      P: 'Current',
      N: 'Startup',
      S: 'Pump-Off/Shutdown',
      SB: 'PO/SD Buffer',
      PSB: 'Prev SD/PO Buffe',
      M: 'Malfunction',
      A: 'Startup (No MPT)',
    };
    const cardTypeName = cardTypeMap[updatedCardDataValues?.CardType] ?? '';

    // Create new card item for the table
    const newCardItem: CardDateItemState = {
      date: updatedCardDataValues.CardDate,
      cardTypeId: updatedCardDataValues.CardType,
      cardTypeName: cardTypeName,
      id: Math.random(),
      isSelected: shouldSelect ?? true,
      color: { r: 0, g: 0, b: 0 },
      downHoleStroke: undefined,
      pumpEfficiency: undefined,
      pumpFillage: updatedCardDataValues.Fillage,
      pumpFillageSetpoint: undefined,
      secondaryFillageSetpoint: undefined,
      inferredProduction: undefined,
      runtime: updatedCardDataValues.Runtime,
      strokesPerMinute: updatedCardDataValues.StrokePerMinute,
      minPolishedRodLoad: undefined,
      maxPolishedRodLoad: undefined,
      fluidPound: undefined,
      buoyantRodWeight: undefined,
      dryRodWeight: undefined,
      pumpFriction: undefined,
      poFluidLoad: undefined,
      upliftOpportunity: undefined,
      rodsOverloaded: undefined,
      pumpTagging: undefined,
      gearboxOverloaded: undefined,
      svLeak: undefined,
      standingValveLeak: undefined,
    };

    // Process downhole load coordinates
    const downHoleLoadCoordinates = [];
    const DownHoleLoadRawX = updatedCardDataValues?.DownHolePosition || [];
    const DownHoleLoadRawY = updatedCardDataValues?.DownHoleLoad || [];
    for (let i = 0; i < DownHoleLoadRawX.length; i++) {
      downHoleLoadCoordinates.push({
        x: DownHoleLoadRawX[i],
        y: DownHoleLoadRawY[i],
      });
    }

    // Process surface load coordinates
    const surfaceLoadCoordinates = [];
    const SurfaceLoadRawX = updatedCardDataValues?.SurfacePosition || [];
    const SurfaceLoadRawY = updatedCardDataValues?.SurfaceLoad || [];
    for (let i = 0; i < SurfaceLoadRawX.length; i++) {
      surfaceLoadCoordinates.push({
        x: SurfaceLoadRawX[i],
        y: SurfaceLoadRawY[i],
      });
    }

    // Create card coordinates for the dynamometer
    const cardCoordinates: Card = {
      cardDate: updatedCardDataValues.CardDate,
      cardTrends: [
        {
          id: 1,
          name: 'Surface Load',
          coordinates: surfaceLoadCoordinates,
        },
        {
          id: 2,
          name: 'POC Downhole Card',
          coordinates: downHoleLoadCoordinates,
        },
        {
          id: 7,
          name: 'High Load Limit',
          coordinates: [{ x: 0, y: updatedCardDataValues.HiLoadLimit || 0 }],
        },
        {
          id: 8,
          name: 'Low Load Limit',
          coordinates: [{ x: 0, y: updatedCardDataValues.LoLoadLimit || 0 }],
        },
      ],
    };

    // Dispatch actions to update the stores
    dispatch(addSingleCardItem({ cardItem: newCardItem, shouldSelect: shouldSelect }));
    dispatch(addSingleCardCoordinates(cardCoordinates));
  };

  // Handle incoming sparkline streaming data from event WebSocket for the selected well
  const handleSparklineStreamingData = (payloadDetails: any) => {
    const isLiveDataEnabled = store.getState().AssetTrendDataSlice.isLiveDataEnabled;
    if (isLiveDataEnabled) {
      const deviceScanData = payloadDetails?.DeviceScanData;
      if (deviceScanData) {
        const NodeId = deviceScanData?.NodeId;
        const currentSelectedAssetName = store.getState().assetGroups.selectedAssetId;
        if (currentSelectedAssetName && NodeId && currentSelectedAssetName.toLowerCase() === NodeId.toLowerCase()) {
          const addressLiveDataMap: Record<string, any> = {};
          const livePointTime = new Date(deviceScanData.UtcScanTime).getTime();
          Object.entries(deviceScanData.Values).forEach(([addressKey, liveValue]: [string, any]) => {
            const livePoint = {
              date: livePointTime,
              value: parseFloat(liveValue),
              index: -1,
              cov: 0,
              cumulativeCOV: 0,
              stdDevValue: 0,
              stdDevNegValue: 0,
              stdDevPosValue: 0,
            };
            addressLiveDataMap[addressKey] = livePoint;
          });
          if (NodeId && Object.keys(addressLiveDataMap).length > 0) {
            console.log('Charts Live points');
            dispatch(addLiveDataPoints({ addressLiveDataMap }));
          }
        }
      }
    }
  };

  const handleLiveCardData = (payloadDetails: any) => {
    if (payloadDetails?.CardDate && payloadDetails?.CardType) {
      const NodeId = payloadDetails?.NodeId;

      if (NodeId) {
        const currentSelectedAssetName = store.getState().assetGroups.selectedAssetId;
        if (currentSelectedAssetName && currentSelectedAssetName.toLowerCase() === NodeId.toLowerCase()) {
          handleGetCardResponse(payloadDetails, false);
        }
      }
    }
  };

  const openSidebar = () => {
    setMouseHasLeftNav(false);
    const shouldExpand = !expandNotificationDrawer && !preventAutoExpand && mouseHasLeftNav;

    if (shouldExpand) {
      setIsExpanded(true);
    }
  };

  return (
    <div
      className={`navigation-bar2-container${navMenuTap ? ' show-nav-menu-icon' : ''} ${isExpanded && 'navigation-bar-expanded'} ${expandNotificationDrawer ? 'has-drawer-open' : ''}`}
    >
      <div
        ref={ref}
        className={`navigation-bar2-main ${expandNotificationDrawer ? 'collapsed' : isExpanded ? 'expanded' : 'collapsed'}`}
        style={{ zIndex: isExpanded ? '9999' : '' }}
        onMouseMove={(e: any) => {
          const targetEl =
            (ref.current as HTMLElement | null) ??
            (e.currentTarget as HTMLElement | null) ??
            (e.srcElement as HTMLElement | null) ??
            (e?.nativeEvent?.target as HTMLElement | null);

          const offsetWidth = (targetEl?.offsetWidth ?? 0) / 2;
          const screenWidth =
            window.innerWidth ?? document.documentElement.clientWidth ?? (window.screen && window.screen.width) ?? 0;
          const mouseX = e?.screenX ?? e?.clientX ?? 0;
          const effectiveThreshold = screenWidth < 768 ? offsetWidth * 1.5 : offsetWidth;

          if (mouseX > effectiveThreshold) return;

          openSidebar();
        }}
        onMouseLeave={() => {
          setMouseHasLeftNav(true);

          const shouldCollapse = !expandNotificationDrawer && !expandedSubMenu && !isMouseOverSubMenu;

          if (shouldCollapse) {
            setIsExpanded(false);
          }
        }}
      >
        <div className='navigation-bar2-top-section'>
          {/* Logo */}
          <div className='navigation-bar2-logo-container'>
            <Link to='/' className='navigation-bar2-logo-image-container'>
              <div
                className={`navigation-bar2-logo-wrapper ${!expandNotificationDrawer && isExpanded ? 'expanded' : 'compact'}`}
              >
                <img id='slb-logo' src={SLBLogo} alt='SLB Logo' className='navigation-bar2-logo-image' />
              </div>
            </Link>
          </div>

          {/* Navigation Items */}
          <div className='navigation-bar2-nav-items'>
            {navigationLoading ? (
              <div className='navigation-bar2-nav-item'>
                <div className='navigation-bar2-nav-item-icon'>
                  {/* Loading placeholder */}
                  <div style={{ width: 24, height: 24, backgroundColor: '#7f97b9ff', borderRadius: '50%' }} />
                </div>
                {showLabels && <div className='navigation-bar2-nav-item-label'>Loading...</div>}
              </div>
            ) : navigationError ? (
              <div className='navigation-bar2-nav-item'>
                <div className='navigation-bar2-nav-item-icon'>
                  {/* Error placeholder */}
                  <div style={{ width: 24, height: 24, backgroundColor: '#ff4444', borderRadius: '50%' }} />
                </div>
                {showLabels && <div className='navigation-bar2-nav-item-label'>Error loading navigation</div>}
              </div>
            ) : (
              navigationItems.map((item) => {
                const IconComponent = getIconComponent(item.key);
                const hasChildren = item.children && item.children.length > 0;

                return (
                  <div key={item.key}>
                    <div
                      className={`navigation-bar2-nav-item ${item.disabled ? 'disabled' : ''} ${hasChildren ? 'has-submenu' : ''}`}
                      onClick={() => handleNavigationItemClick(item)}
                      onMouseEnter={() => {
                        openSidebar();
                      }}
                    >
                      <div className='navigation-bar2-nav-item-icon'>
                        <IconComponent width={24} height={24} stroke={item.disabled ? '#7f97b9ff' : '#E2E8F0'} />
                      </div>
                      {showLabels && <div className='navigation-bar2-nav-item-label'>{item.label}</div>}
                      {hasChildren &&
                        item.key !== 'notifications' &&
                        item.key !== 'recommendations' &&
                        item.key !== 'customViews' &&
                        isExpanded &&
                        !expandNotificationDrawer && (
                          <div className='navigation-bar2-nav-item-chevron'>
                            <ChevronRightIcon width={16} height={16} stroke={item.disabled ? '#7f97b9ff' : '#E2E8F0'} />
                          </div>
                        )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className='navigation-bar2-bottom-section'>
          {AuthService.isUserLoggedInLocal() && (
            <div className='navigation-bar2-bottom-items'>
              {footerItems.find((item) => item.key === 'support') &&
                footerItems
                  .filter((item) => item.key === 'support')
                  .map((item) => {
                    const IconComponent = getIconComponent(item.key);
                    return (
                      <div
                        key={item.key}
                        className='navigation-bar2-nav-item'
                        onMouseEnter={() => {
                          openSidebar();
                        }}
                      >
                        <div className='navigation-bar2-nav-item-icon'>
                          <IconComponent width={24} height={24} stroke='#F4FBFC' />
                        </div>
                        {showLabels && <div className='navigation-bar2-nav-item-label'>{item.label}</div>}
                      </div>
                    );
                  })}

              {/* {footerItems.find((item) => item.key === 'settings') &&
                footerItems
                  .filter((item) => item.key === 'settings')
                  .map((item) => {
                    const IconComponent = getIconComponent(item.key);
                    return (
                      <div
                        key={item.key}
                        className='navigation-bar2-nav-item'
                        onMouseEnter={() => {
                          openSidebar();
                        }}
                      >
                        <div className='navigation-bar2-nav-item-icon'>
                          <IconComponent width={24} height={24} stroke='#F4FBFC' />
                        </div>
                        {showLabels && <div className='navigation-bar2-nav-item-label'>{item.label}</div>}
                      </div>
                    );
                  })} */}

              {!isCloud && hostingEnvironment && isSSOUser === false && (
                <div
                  className='navigation-bar2-nav-item'
                  onClick={handleChangePasswordClick}
                  onMouseEnter={() => {
                    openSidebar();
                  }}
                >
                  <div className='navigation-bar2-nav-item-icon'>
                    <PasswordChangeIcon width={24} height={24} stroke='#F4FBFC' />
                  </div>
                  {showLabels && <div className='navigation-bar2-nav-item-label'>Change password</div>}
                </div>
              )}

              {!isCloud && isAdmin && (
                <div
                  className='navigation-bar2-nav-item'
                  onClick={handleOnboardingClick}
                  onMouseEnter={() => {
                    openSidebar();
                  }}
                >
                  <div className='navigation-bar2-nav-item-icon'>
                    <OnboardingIcon width={24} height={24} stroke='#F4FBFC' />
                  </div>
                  {showLabels && <div className='navigation-bar2-nav-item-label'>Onboarding</div>}
                </div>
              )}

              <div
                className='navigation-bar2-nav-item'
                onClick={handleLogoutClick}
                onMouseEnter={() => {
                  openSidebar();
                }}
              >
                <div className='navigation-bar2-nav-item-icon'>
                  <CollapseIcon width={24} height={24} stroke='#F4FBFC' />
                </div>
                {showLabels && <div className='navigation-bar2-nav-item-label'>Sign out</div>}
              </div>
            </div>
          )}
        </div>
      </div>

      {expandedSubMenu &&
        isExpanded &&
        !expandNotificationDrawer &&
        (() => {
          const selectedItem = navigationItems.find((item) => item.key === expandedSubMenu);
          return selectedItem && selectedItem.children && selectedItem.children.length > 0 ? (
            <div
              className='navigation-bar2-submenu'
              onMouseEnter={() => setIsMouseOverSubMenu(true)}
              onMouseLeave={() => {
                setIsMouseOverSubMenu(false);
                // Close sub-menu and collapse navigation when mouse leaves sub-menu
                setExpandedSubMenu(null);
                setIsExpanded(false);
              }}
            >
              {selectedItem.children.map((subItem: any) => (
                <div
                  key={subItem.key}
                  className={`navigation-bar2-submenu-item ${subItem.disabled ? 'disabled' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSubMenuItemClick(selectedItem.key, subItem);
                  }}
                >
                  <div className='navigation-bar2-submenu-item-label'>{subItem.label}</div>
                </div>
              ))}
            </div>
          ) : null;
        })()}

      {/* Drawer components */}
      <div>{expandNotificationDrawer && <NotificationDrawerItem />}</div>
    </div>
  );
};

export default AllyNavigation;
